// Messages — in-platform conversations with internal staff.
// Staff: two-pane inbox (thread list + conversation). Clients/reps: their single conversation.
// New inbound messages email all internal staff (handled server-side).
import { useEffect, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import { fetchThreads, fetchThread, sendMessage } from '../api/messages.js';
import { useAuthStore } from '../stores/authStore.js';

const timeLabel = (d) => (d ? new Date(d).toLocaleString() : '');

function Conversation({ threadId, onSent, emptyHint }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  const load = () => {
    if (!threadId) {
      setData({ id: null, messages: [] });
      return;
    }
    fetchThread(threadId)
      .then(setData)
      .catch(() => setData({ id: threadId, messages: [] }));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [data]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setError('');
    try {
      await sendMessage({ ...(threadId ? { threadId } : {}), body: text });
      setBody('');
      load();
      onSent?.();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not send.');
    } finally {
      setBusy(false);
    }
  };

  const messages = data?.messages || [];

  return (
    <div className="flex h-full min-h-[420px] flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center text-sm text-text-muted">
            {emptyHint || 'No messages yet. Say hello 👋'}
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.fromStaff ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.fromStaff ? 'bg-surface-raised text-text-primary' : 'bg-accent text-white'}`}>
                <div className="mb-0.5 text-[10px] opacity-70">
                  {m.senderName} · {timeLabel(m.at)}
                </div>
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t border-line p-3">
        {error && <p className="mb-1 text-xs text-error">{error}</p>}
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
            }}
            rows={2}
            placeholder="Write a message… (⌘/Ctrl+Enter to send)"
            className="min-w-0 flex-1 resize-none rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
          />
          <button
            onClick={send}
            disabled={busy || !body.trim()}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Messages() {
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === 'INTERNAL_STAFF';
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadThreads = () =>
    fetchThreads()
      .then((t) => {
        setThreads(t);
        setActiveId((cur) => cur || t[0]?.id || null);
      })
      .catch(() => setThreads([]))
      .finally(() => setLoading(false));
  useEffect(() => {
    loadThreads();
  }, []);

  // Client / rep: a single conversation with the TEAMM8 team.
  if (!isStaff) {
    return (
      <>
        <PageHeader title="Messages" subtitle="Message the TEAMM8 team directly — we'll reply here and by email." />
        <div className="rounded-lg border border-line bg-surface shadow-sm">
          <Conversation
            threadId={threads[0]?.id || null}
            onSent={loadThreads}
            emptyHint="No messages yet. Send us a question about pricing, orders, or shipping."
          />
        </div>
      </>
    );
  }

  // Staff: inbox + conversation.
  return (
    <>
      <PageHeader title="Messages" subtitle="Client & rep conversations. New messages also email the whole internal team." />
      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <div className="rounded-lg border border-line bg-surface shadow-sm">
          <div className="border-b border-line px-3 py-2 text-xs font-medium text-text-muted">
            {threads.length} conversation{threads.length === 1 ? '' : 's'}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-text-muted">Loading…</div>
            ) : threads.length === 0 ? (
              <div className="p-4 text-sm text-text-muted">No conversations yet.</div>
            ) : (
              threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveId(t.id)}
                  className={`flex w-full flex-col gap-0.5 border-b border-line px-3 py-2 text-left transition last:border-0 hover:bg-surface-raised ${
                    activeId === t.id ? 'bg-surface-raised' : ''
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-text-primary">
                    {t.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" title="Unread" />}
                    <span className="truncate">{t.subject?.name || t.subject?.email || 'Client'}</span>
                    <span className="ml-auto shrink-0 rounded-full bg-surface px-1.5 text-[10px] text-text-muted">
                      {t.subject?.role === 'REP' ? 'Rep' : 'Client'}
                    </span>
                  </span>
                  <span className="truncate text-xs text-text-muted">{t.lastMessagePreview || 'No messages yet'}</span>
                </button>
              ))
            )}
          </div>
        </div>
        <div className="rounded-lg border border-line bg-surface shadow-sm">
          {activeId ? (
            <Conversation key={activeId} threadId={activeId} onSent={loadThreads} />
          ) : (
            <div className="flex h-full min-h-[420px] items-center justify-center text-sm text-text-muted">
              Select a conversation.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
